import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import FertilizerIngredientModal from "./FertilizerIngredientModal";
import { axiosInstance, buildLegacyHeaders } from "../../axiosFetchers";
import { useAuth } from "../../../Components/Auth/useAuth";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(920px, calc(100vw - 32px))",
  maxHeight: "calc(100vh - 32px)",
  overflowY: "auto",
  bgcolor: "background.paper",
  border: "1px solid",
  borderColor: "grey.300",
  borderRadius: "8px",
  boxShadow: 18,
  p: { xs: 2, md: 3 },
};

export default function FertilizeMixModal({ open, handleClose, handleSave }) {
  const [mixName, setMixName] = useState("");
  const [rows, setRows] = useState([]);
  const [totalPounds, setTotalPounds] = useState(0);
  const { user } = useAuth();
  const [NPKS, setNPKS] = useState("0-0-0-0");
  const [fertilizerIngredients, setFertilizerIngredients] = useState([]);
  const [fertilizerIngredientsDict, setFertilizerIngredientsDic] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    async function fetchFertIngredients() {
      try {
        const response = await axiosInstance.get(
          process.env.REACT_APP_API_URL +
            `/assessments/fertilizer_ingredients`,
          {
            headers: buildLegacyHeaders(user),
          }
        );

        setFertilizerIngredients(response.data);
        const tmpDict = {};
        for (const fert of response.data) {
          tmpDict[fert.id] = fert;
        }
        setFertilizerIngredientsDic(tmpDict);
      } catch (error) {
        setErrorMessage("Fertilizer ingredients could not be loaded.");
      }
    }
    fetchFertIngredients();
    return undefined;
  }, [open, user]);

  useEffect(() => {
    if (!open) {
      setMixName("");
      setRows([]);
      setErrorMessage("");
      setIsSaving(false);
    }
  }, [open]);

  const handlePostIngredient = async (ingredient) => {
    const response = await axiosInstance.post(
      process.env.REACT_APP_API_URL + `/assessments/fertilizer_ingredients`,
      ingredient,
      {
        headers: buildLegacyHeaders(user),
      }
    );

    setFertilizerIngredients([...fertilizerIngredients, response.data]);

    let newObj = {};
    newObj[response.data.id] = response.data;

    setFertilizerIngredientsDic({
      ...fertilizerIngredientsDict,
      ...newObj,
    });

    return response.data.id;
  };

  const handleChange = (index, data) => {
    const cpyRows = [...rows];
    cpyRows.splice(index, 1, data);

    setRows(cpyRows);
  };
  const handleDelete = (index) => {
    const cpyRows = [...rows];
    cpyRows.splice(index, 1);

    setRows(cpyRows);
  };

  const handleAddFertilizerIngredient = () => {
    const cpy = [...rows];
    cpy.push({ ingredient_id: "", amount_in_pounds: 0 });

    setRows(cpy);
  };
  function precise(num) {
    return Math.round(num * 100) / 100;
  }

  useEffect(() => {
    let total = 0;
    for (const row of rows) {
      total += Number(row.amount_in_pounds) || 0;
    }
    let lbs = 0;

    const pseudoNpks = { N: 0, P: 0, K: 0, S: 0 };

    for (const row of rows) {
      const ingredient = fertilizerIngredientsDict[row.ingredient_id];
      const amount = Number(row.amount_in_pounds) || 0;
      if (ingredient && total > 0) {
        pseudoNpks.N += ingredient.n_value * (amount / total);
        pseudoNpks.P += ingredient.p_value * (amount / total);
        pseudoNpks.K += ingredient.k_value * (amount / total);
        pseudoNpks.S += ingredient.s_value * (amount / total);
        lbs += amount;
      }
    }
    setTotalPounds(lbs);
    setNPKS(
      total > 0
        ? `${precise(pseudoNpks.N)}-${precise(pseudoNpks.P)}-${precise(
            pseudoNpks.K
          )}-${precise(pseudoNpks.S)}`
        : "0-0-0-0"
    );
  }, [rows, fertilizerIngredientsDict]);

  const hasValidRows =
    rows.length > 0 &&
    rows.every(
      (row) =>
        Boolean(row.ingredient_id) &&
        Number.isFinite(Number(row.amount_in_pounds)) &&
        Number(row.amount_in_pounds) > 0
    );
  const ingredientIds = rows.map((row) => row.ingredient_id).filter(Boolean);
  const hasUniqueIngredients =
    ingredientIds.length === new Set(ingredientIds).size;
  const isSaveDisabled =
    !mixName.trim() || !hasValidRows || !hasUniqueIngredients || isSaving;

  async function handleSubmit() {
    setIsSaving(true);
    setErrorMessage("");
    try {
      await handleSave({
        name: mixName.trim(),
        ingredients: rows.map((row) => ({
          ingredient_id: row.ingredient_id,
          amount_in_pounds: Number(row.amount_in_pounds),
        })),
      });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setErrorMessage(
        typeof detail === "string"
          ? detail
          : "The fertilizer mix could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={() => handleClose(false)}>
      <Box sx={style}>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
            Create a Fertilizer Mix
          </Typography>
          <IconButton
            aria-label="Close fertilizer mix dialog"
            onClick={() => handleClose(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" component="div">
            Mix Name
          </Typography>

          <TextField
            fullWidth
            label="Mix name"
            value={mixName}
            onChange={(e) => setMixName(e.target.value)}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" component="div">
            Mix Ingredients
          </Typography>

          <TableContainer component={Paper}>
            <Table aria-label="Fertilizer mix ingredients" sx={{ minWidth: 780 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Fertilizer Name</TableCell>
                  <TableCell>Amount Applied (Lbs)</TableCell>
                  <TableCell>Units</TableCell>
                  <TableCell>Total N</TableCell>
                  <TableCell>Total P</TableCell>
                  <TableCell>Total K</TableCell>
                  <TableCell>Total S</TableCell>
                  <TableCell>Options</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <IngredientRow
                    key={index}
                    data={row}
                    index={index}
                    handleChange={handleChange}
                    handleDelete={handleDelete}
                    handlePostIngredient={handlePostIngredient}
                    fertilizerIngredients={fertilizerIngredients}
                    fertilizerIngredientsDict={fertilizerIngredientsDict}
                  />
                ))}
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Button onClick={handleAddFertilizerIngredient}>
                      Add Fertilizer to Mix
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" component="div">
            Your Summary
          </Typography>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 520 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Total Pounds for Field</TableCell>
                  <TableCell>NPKS</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{totalPounds || ""}</TableCell>
                  <TableCell>{NPKS}</TableCell>
                  <TableCell>
                    <Button
                      aria-label="Save fertilizer mix"
                      disabled={isSaveDisabled}
                      onClick={handleSubmit}
                      startIcon={<SaveIcon />}
                      variant="contained"
                    >
                      {isSaving ? "Saving..." : "Save mix"}
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {!hasValidRows && rows.length > 0 ? (
          <Typography color="error" sx={{ mt: 2 }} variant="body2">
            Select an ingredient and enter an amount greater than zero for every
            row.
          </Typography>
        ) : null}
        {hasValidRows && !hasUniqueIngredients ? (
          <Typography color="error" sx={{ mt: 2 }} variant="body2">
            Use each ingredient only once per mix.
          </Typography>
        ) : null}
        {errorMessage ? (
          <Typography color="error" role="alert" sx={{ mt: 2 }}>
            {errorMessage}
          </Typography>
        ) : null}
      </Box>
    </Modal>
  );
}
function IngredientRow({
  data,
  index,
  handleChange,
  handleDelete,
  handlePostIngredient,
  fertilizerIngredients,
  fertilizerIngredientsDict,
}) {
  const [localData, setLocalData] = useState(data);
  const [ingredientModalOpen, setIngredientModalOpen] = useState(false);

  useEffect(() => {
    setLocalData(data);
  }, [data, fertilizerIngredients, fertilizerIngredientsDict]);

  const handleSelectChange = (event) => {
    const selectedId = event.target.value;

    const updatedLocalData = { ...localData, ingredient_id: selectedId };

    handleChange(index, updatedLocalData);
  };
  const handleAmountChange = (event) => {
    const newAmount =
      event.target.value === "" ? "" : Number(event.target.value);
    const updatedLocalData = { ...localData, amount_in_pounds: newAmount };

    handleChange(index, updatedLocalData);
  };

  async function handleAddNewIngredient(ingredient) {
    const id = await handlePostIngredient(ingredient);
    handleChange(index, { ...localData, ingredient_id: id });
    setIngredientModalOpen(false);
  }

  if (
    localData.ingredient_id &&
    !(localData.ingredient_id in fertilizerIngredientsDict)
  )
    return null;
  return (
    <TableRow>
      <TableCell>
        <Select
          inputProps={{ "aria-label": `Mix ingredient #${index + 1}` }}
          value={localData.ingredient_id || ""}
          onChange={handleSelectChange}
          displayEmpty
          fullWidth
        >
          <MenuItem value="" disabled>
            Select Fertilizer
          </MenuItem>
          {fertilizerIngredients.map((fertilizer) => (
            <MenuItem key={fertilizer.id} value={fertilizer.id}>
              {fertilizer.name +
                " " +
                `(${fertilizer.n_value}-${fertilizer.p_value}-${fertilizer.k_value}-${fertilizer.s_value})`}
            </MenuItem>
          ))}
          <MenuItem>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setIngredientModalOpen(true);
              }}
            >
              Add Ingredient
            </Button>
          </MenuItem>

          <FertilizerIngredientModal
            open={ingredientModalOpen}
            handleClose={() => setIngredientModalOpen(false)}
            handleAddNewIngredient={handleAddNewIngredient}
          />
        </Select>
      </TableCell>
      <TableCell>
        <TextField
          inputProps={{
            "aria-label": `Mix ingredient #${index + 1} amount`,
            min: 0,
            step: "any",
          }}
          type="number"
          value={localData.amount_in_pounds ?? ""}
          onChange={handleAmountChange}
          fullWidth
        />
      </TableCell>
      <TableCell>total lb</TableCell>
      <TableCell>
        {parseInt(
          localData.ingredient_id &&
            (fertilizerIngredientsDict[localData.ingredient_id]["n_value"] /
              100) *
              localData.amount_in_pounds
        ) || ""}
      </TableCell>
      <TableCell>
        {parseInt(
          localData.ingredient_id &&
            (fertilizerIngredientsDict[localData.ingredient_id]["p_value"] /
              100) *
              localData.amount_in_pounds
        ) || ""}
      </TableCell>
      <TableCell>
        {parseInt(
          localData.ingredient_id &&
            (fertilizerIngredientsDict[localData.ingredient_id]["k_value"] /
              100) *
              localData.amount_in_pounds
        ) || ""}
      </TableCell>
      <TableCell>
        <IconButton
          aria-label={`Delete mix ingredient #${index + 1}`}
          onClick={() => handleDelete(index)}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </TableCell>
      <TableCell>
        {parseInt(
          localData.ingredient_id &&
            (fertilizerIngredientsDict[localData.ingredient_id]["s_value"] /
              100) *
              localData.amount_in_pounds
        ) || ""}
      </TableCell>
    </TableRow>
  );
}
